import React, { useEffect, useRef, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AppContext } from "../App";
import { 
  FaUserShield, FaEye, FaHandshake, FaLightbulb, FaCheckDouble, 
  FaClock, FaArrowRight, FaChevronLeft, FaChevronRight, FaFileContract,
  FaChartLine, FaShieldHeart, FaUsersGear, FaGlobe, FaBuildingColumns,
  FaAward, FaCircleCheck, FaArrowUpRightDots, FaUserTie, FaGraduationCap
} from "react-icons/fa6";

// Assets
import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";
import banner3 from "../assets/banner3.jpg";
import banner4 from "../assets/banner4.jpg";
import marcelino from "../assets/marcelino.png";
import mary from "../assets/mary.png";
import newton from "../assets/newton.png";

const sliderImages = [
  { img: banner1, label: "Strategic Business Advisory" },
  { img: banner2, label: "Compliance & Taxation" },
  { img: banner3, label: "Financial Management" },
  { img: banner4, label: "Risk & Control Strategy" }
];

const teamMembers = [
  {
    name: "CPA Marcelino Kilyenyi",
    title: "Director",
    credentials: "CFIP, ACPA-PP, B.Com",
    bio: "Marcelino is a Certified Public Accountant, Auditor, and Forensic Expert with over a decade of experience. He specialized in designing effective internal controls and project supervision for NGOs and the private sector.",
    highlights: ["Forensic Investigation (IICFIP)", "NBAA Member", "M&E Specialist"],
    image: marcelino
  },
  {
    name: "CPA Newton Mathew",
    title: "Director",
    credentials: "ACPA-PP, B. Com, Tax Consultant",
    bio: "Newton is a registered Tax Consultant with the TRA. He has a proven track record of reconciling billions in overdue debts and designing credit/market risk controls within the banking and energy sectors.",
    highlights: ["TRA Registered", "Strategic Planning", "Debt Recovery Expert"],
    image: newton
  },
  {
    name: "Ms. Mary Leon Simbakalia",
    title: "Senior Consultant",
    credentials: "ACMA, CGMA",
    bio: "A Chartered Management Accountant with 20+ years of experience including PwC and NIC. Mary is a 'cost champion' known for optimizing business models and facilitating Foreign Direct Investments (FDIs).",
    highlights: ["CIMA UK Member", "FDI Mobilization", "Business Performance"],
    image: mary
  }
];

const coreValues = [
  { letter: "A", title: "Accountability", desc: "Fully responsible and accountable for professional work.", icon: <FaUserShield /> },
  { letter: "T", title: "Transparency", desc: "Open communication with strict confidentiality.", icon: <FaClock /> },
  { letter: "R", title: "Responsiveness", desc: "Prompt, value-driven client engagement.", icon: <FaHandshake /> },
  { letter: "I", title: "Integrity", desc: "Honest and straightforward business conduct.", icon: <FaEye /> },
  { letter: "C", title: "Competency", desc: "High standards of skill and expert knowledge.", icon: <FaCheckDouble /> }
];

const ATRIC_BCL_MasterPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const appContext = useContext(AppContext);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight / 2;
      const elements = document.querySelectorAll(".atric-v-node");
      elements.forEach((el, idx) => {
        if (el.offsetTop < scrollPos) setActiveStep(idx);
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide(s => (s + 1) % sliderImages.length), 7000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="atric-supreme-wrapper">
      
      {/* --- 01. MODERN CENTERED HERO --- */}
      <section className="atric-hero-centered">
        <div className="hero-slider-bg">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentSlide}
              className="hero-bg-img"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
              style={{ backgroundImage: `url(${sliderImages[currentSlide].img})` }}
            />
          </AnimatePresence>
          <div className="hero-overlay-gradient" />
        </div>

        <div className="container h-100 d-flex align-items-center justify-content-center">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-content-glass text-center"
          >
            <h1 className="hero-main-title">
              ATRIC Business <br className="d-none d-md-block" />
              <span className="text-gradient">Consulting Ltd.</span>
            </h1>
            <p className="hero-sub-text mx-auto font-semi-bold">
              Specialized financial and business advisors supporting profitability, 
              operational efficiency, and risk management through 
              strategic excellence.
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-3 mt-4">
              <button className="btn-hero-solid" onClick={() => appContext.navTo({ item: "contact_us" })}>
                Get Started <FaArrowRight className="ms-2" />
              </button>
            </div>
          </motion.div>
        </div>

        <div className="hero-nav-bottom d-none">
          <div className="d-flex align-items-center gap-3">
            {sliderImages.map((_, i) => (
              <button key={i} onClick={() => setCurrentSlide(i)} className={`hero-dot ${i === currentSlide ? "active" : ""}`} />
            ))}
          </div>
          <p className="mt-3 text-white-50 small font-bold tracking-widest text-uppercase">{sliderImages[currentSlide].label}</p>
        </div>
      </section>

      {/* --- 02. INTRODUCTION & VISION SPLIT --- */}
      <section className="atric-intro py-5">
        <div className="container">
          <div className="row g-5 align-items-center my-4 py-5">
              <div className="col-lg-5">
              <div className="atric-vm-card p-5 bg-dark text-white rounded-4 shadow-xl">
                <FaEye className="text-primary mb-4" size={40} />
                <h3 className="font-bold mb-3 text-white">Our Vision</h3>
                <p className="opacity-75">To be the leading Consulting Company in Africa.</p>
                <hr className="my-4 opacity-25" />
                <h3 className="font-bold mb-3 text-white">Our Mission</h3>
                <p className="opacity-75">Providing quality, timely services to grow businesses and projects sustainably.</p>
              </div>
            </div>
            <div className="col-lg-7">
              <h2 className="atric-h2 mb-4">Connecting Strategy with the <span className="text-blue">Business Model.</span></h2>
              <p className="atric-p mb-4">
                Incorporated in 2022, ATRIC BCL provides management consulting to support CEOs, CFOs, and Boards. 
                Affiliated with <strong>ATRIC Consultants (PF 407)</strong>, we leverage over a decade of expertise 
                to facilitate Foreign Direct Investments and access to competitive loans.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- 03. LEADERSHIP TEAM (REFINED INTERFACE) --- */}
      <section className="atric-team py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="text-blue fw-bold text-uppercase tracking-widest mb-2">Leadership</h6>
            <h2 className="atric-h2">Expertise Behind Our Success</h2>
            <div className="h-line mx-auto" />
          </div>

          <div className="row g-4">
            {teamMembers.map((member, idx) => (
              <div className="col-lg-4" key={idx}>
                <motion.div 
                  whileHover={{ y: -10 }}
                  className="team-card p-4 rounded-4 border h-100 shadow-sm text-center"
                >
                  <div className="position-relative mb-4 d-inline-block">
                    <div className="avatar-frame shadow-sm">
                      <img src={member.image} alt={member.name} className="img-fluid" />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="fw-bold mb-1 text-dark">{member.name}</h5>
                    <span className="small text-blue fw-bold text-uppercase">{member.title}</span>
                  </div>

                  <div className="credentials-chip mb-3 mx-auto">
                    <FaGraduationCap className="me-2" /> {member.credentials}
                  </div>
                  
                  <p className="small text-muted mb-4 line-height-relaxed px-2">
                    {member.bio}
                  </p>
                  
                  <div className="mt-auto pt-3 border-top border-light">
                    {member.highlights.map((h, i) => (
                      <div key={i} className="small fw-bold text-dark mb-1 d-flex align-items-center justify-content-start">
                        <FaCircleCheck className="text-blue me-2" size={12} /> {h}
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 04. CONSULTING SERVICES --- */}
      <section className="atric-services bg-light py-5">
        <div className="container">
          <div className="text-center mb-5 pb-lg-4">
            <h2 className="atric-h2 mb-3">Our Consulting Services</h2>
            <div className="h-line mx-auto" />
          </div>
          <div className="row g-4">
            {[
              { t: "Strategy Review", d: "Analysis of Business, Finance, and Risk Management strategies.", i: <FaArrowUpRightDots /> },
              { t: "Management Accounting", d: "BCG Matrix, Porter's Value Chain, and Balanced Scorecards.", i: <FaBuildingColumns /> },
              { t: "FDI Facilitation", d: "Facilitating foreign loans and technology transfer for growth.", i: <FaGlobe /> },
              { t: "Internal Audit", d: "Cyber risk reporting, risk registers, and governance assessment.", i: <FaShieldHeart /> },
              { t: "TISEZA Support", d: "Applications for Certificates of Incentives and Strategic Status.", i: <FaFileContract /> },
              { t: "Change Management", d: "Harmonizing operations during mergers and restructuring.", i: <FaUsersGear /> },
              { t: "Business Advisory & Training", d: "Harmonizing operations during mergers and restructuring.", i: <FaUsersGear /> }
            ].map((s, idx) => (
              <div className="col-lg-4 col-md-6" key={idx}>
                <motion.div whileHover={{ y: -8 }} className="atric-tile p-4 h-100 rounded-4 border bg-white shadow-sm">
                  <div className="tile-icon mb-3 text-blue">{s.i}</div>
                  <h5 className="fw-bold mb-2">{s.t}</h5>
                  <p className="small text-muted mb-0">{s.d}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        .atric-supreme-wrapper { font-family: 'Inter', sans-serif; background: #fff; overflow-x: hidden; }
        .text-blue { color: #0284c7 !important; }
        .text-gradient { background: linear-gradient(to right, #fff, #38bdf8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .h-line { width: 60px; height: 4px; background: #0284c7; border-radius: 2px; }

        /* --- Modern Centered Hero --- */
        .atric-hero-centered { height: 100vh; position: relative; display: flex; align-items: center; overflow: hidden; background: #000; }
        .hero-slider-bg { position: absolute; inset: 0; z-index: 1; }
        .hero-bg-img { width: 100%; height: 100%; background-size: cover; background-position: center; }
        .hero-overlay-gradient { position: absolute; inset: 0; background: radial-gradient(circle, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.9) 100%); }
        .hero-content-glass { position: relative; z-index: 2; max-width: 900px; padding: 3rem; margin: 0 1rem; }
        .hero-main-title { font-size: clamp(2.5rem, 8vw, 4.5rem); font-weight: 900; color: #fff; line-height: 1.1; margin-bottom: 1.5rem; letter-spacing: -2px; }
        .hero-sub-text { font-size: 1.25rem; color: #cbd5e1; max-width: 650px; line-height: 1.6; }
        .btn-hero-solid { background: #fff; color: #0f172a; border: none; padding: 18px 45px; border-radius: 12px; font-weight: 800; transition: 0.3s ease; }
        .btn-hero-solid:hover { background: #0284c7; color: #fff; transform: translateY(-5px); box-shadow: 0 15px 30px rgba(2, 132, 199, 0.4); }

        .hero-nav-bottom { position: absolute; bottom: 40px; left: 0; right: 0; z-index: 10; display: flex; flex-direction: column; align-items: center; }
        .hero-dot { width: 40px; height: 4px; background: rgba(255, 255, 255, 0.2); border: none; border-radius: 2px; transition: 0.4s; }
        .hero-dot.active { background: #0284c7; width: 60px; }

        /* --- Refined Team Interface --- */
        .avatar-frame { width: 150px; height: 150px; border-radius: 50%; overflow: hidden; border: 4px solid #f8fafc; margin: 0 auto; background: #fff; }
        .avatar-frame img { width: 100%; height: 100%; object-fit: cover; }
        .team-card { transition: 0.4s ease; background: #fff; border: 1px solid #f1f5f9; }
        .team-card:hover { border-color: #0284c7; }
        .credentials-chip { display: inline-flex; align-items: center; padding: 6px 16px; background: #f0f9ff; color: #0369a1; border-radius: 50px; font-size: 0.75rem; font-weight: 800; }
        .line-height-relaxed { line-height: 1.6; }

        /* --- Global Sections --- */
        .atric-h2 { font-weight: 900; font-size: 2.5rem; color: #0f172a; letter-spacing: -1px; }
        .atric-p { font-size: 1.1rem; color: #64748b; line-height: 1.8; }
        .atric-tile { transition: 0.3s ease; border-color: #f1f5f9; cursor: pointer; }
        .atric-tile:hover { transform: translateY(-10px); border-color: #0284c7; box-shadow: 0 20px 40px rgba(0,0,0,0.05); }

        .atric-path-container { position: relative; max-width: 850px; margin: 0 auto; }
        .path-line { position: absolute; left: 50%; top: 0; bottom: 0; width: 4px; background: #f1f5f9; transform: translateX(-50%); }
        .path-filler { width: 100%; background: #0284c7; transition: 0.6s ease; }
        .atric-v-node { position: relative; margin-bottom: 40px; display: flex; align-items: center; transition: 0.5s; opacity: 0.2; }
        .atric-v-node.active { opacity: 1; }
        .v-marker { position: absolute; left: 50%; transform: translateX(-50%); width: 45px; height: 45px; background: #fff; border: 4px solid #f1f5f9; border-radius: 50%; display: flex; align-items: center; justify-content: center; z-index: 5; font-weight: 900; color: #0284c7; }
        .v-content-wrap { width: 50%; padding: 0 40px; }
        .atric-v-node.right { flex-direction: row-reverse; }

        @media (max-width: 768px) {
          .hero-main-title { font-size: 2.8rem; }
          .hero-content-glass { padding: 2rem 1.5rem; }
          .path-line, .v-marker { left: 20px; transform: none; }
          .v-content-wrap { width: 100%; padding-left: 60px; }
          .atric-v-node.right { flex-direction: row; }
        }
      `}</style>
    </div>
  );
};

export default ATRIC_BCL_MasterPage;